import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '../../primitives/button';
import { FormMessage } from '../../primitives/form';
import { Label } from '../../primitives/label';
import { ScrollArea, ScrollBar } from '../../primitives/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../primitives/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../primitives/sheet';

import { InputField } from '../input/input-field';
import { LabelWithoutRef } from '../label/label-without-ref';
import type { TObjectPath } from './types';
import { normalizePathForUI, normalizePathToStored } from './utils';

type TCreateCustomPathSheetProps = {
  open: boolean;
  existingPaths: TObjectPath[];
  pathTypeOptions: string[];
  initialPath?: string;
  onOpenChange: (open: boolean) => void;
  onSave: (customPath: TObjectPath) => void;
};

export function CreateCustomPathSheet({
  open,
  existingPaths,
  pathTypeOptions,
  initialPath,
  onOpenChange,
  onSave,
}: TCreateCustomPathSheetProps) {
  const [path, setPath] = useState('');
  const [pathType, setPathType] = useState('');

  useEffect(() => {
    if (!open) return;
    setPath(normalizePathForUI(initialPath?.trim() ?? ''));
    setPathType('');
  }, [initialPath, open]);

  const normalizedUIPath = path.trim();
  const storedPath = normalizePathToStored(normalizedUIPath);

  const existingPathNames = useMemo(
    () => new Set(existingPaths.map((item) => item.path.trim().toLowerCase())),
    [existingPaths],
  );

  const isDuplicatedPath =
    storedPath.length > 0 && existingPathNames.has(storedPath.toLowerCase().trim());

  const canSave =
    storedPath.length > 0 &&
    pathType.length > 0 &&
    pathType !== 'object' &&
    !isDuplicatedPath &&
    storedPath !== '$.';

  const handleSave = useCallback(() => {
    if (!canSave) return;

    setPath('');
    setPathType('');
    onOpenChange(false);

    onSave({
      path: storedPath,
      pathType,
    });
  }, [canSave, onOpenChange, onSave, pathType, storedPath]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex size-full flex-col">
        <SheetHeader>
          <SheetTitle>Crear path</SheetTitle>
          <SheetDescription className="text-pretty">
            Un path permite acceder a un valor específico dentro de un fact de tipo objeto.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1">
          <div className="grid gap-4 px-[1px]">
            <div className="rounded-lg border border-input bg-muted/50 p-3 text-xs text-muted-foreground">
              <p className="pb-2 pl-1 font-medium">Ejemplo</p>
              <pre className="overflow-x-auto rounded-md bg-background px-3.5 py-3 font-mono">
                {`fact_001: {
  theme: "dark",
  location: {
    latitude: 19.36435304186946,
  }
}
`}
              </pre>

              <div className="space-y-1 pt-2 pl-1">
                <ul>
                  <li>Path: theme</li>
                  <li>
                    Path: location<span className="font-bold text-accent">.</span>latitude
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="custom-path-name">Path</Label>
              <InputField
                autoComplete="off"
                className={isDuplicatedPath ? 'border-destructive' : undefined}
                counter={{ enable: true }}
                id="custom-path-name"
                placeholder="location.city"
                value={path}
                onChange={(event) => setPath(event.target.value)}
              />
              {isDuplicatedPath && <FormMessage>Este path ya existe en la lista.</FormMessage>}
            </div>

            <div className="space-y-1">
              <LabelWithoutRef>Tipo de path</LabelWithoutRef>
              <Select value={pathType} onValueChange={setPathType}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {pathTypeOptions.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <ScrollBar orientation="vertical" />
        </ScrollArea>

        <SheetFooter>
          <Button
            title="Cancelar"
            className="w-full"
            variant="destructive"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button title="Crear" className="w-full" disabled={!canSave} onClick={handleSave}>
            Crear
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
